import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter } from 'rxjs';

// Tipos de mensajes WebSocket
export interface UserPublicInfo {
  id: string;
  name: string;
  app_type: string;
  validation_level: number;
  reputation: number;
}

export type ClientMessage = 
  | { type: "JoinChatRoom", data: { room_id: string } }
  | { type: "LeaveChatRoom", data: { room_id: string } }
  | { type: "SendChatMessage", data: { room_id: string, content: string, message_type: string } }
  | { type: "EnableProximityNotifications", data: { radius_meters: number } }
  | { type: "DisableProximityNotifications" }
  | { type: "UpdateLocation", data: { latitude: number, longitude: number } }
  | { type: "SubscribeToPlace", data: { place_id: string } }
  | { type: "UnsubscribeFromPlace", data: { place_id: string } }
  | { type: "SubscribeToMenu", data: { company_id: number } }
  | { type: "UnsubscribeFromMenu", data: { company_id: number } }
  | { type: "SubscribeToOrderStatus", data: { session_id: string } }
  | { type: "UnsubscribeFromOrderStatus", data: { session_id: string } }
  | { type: "Ping" };

export type ServerMessage = 
  | {
      type: "ChatMessage",
      data: {
        room_id: string,
        message_id: string,
        user_id: string,
        user_info: UserPublicInfo,
        content: string,
        message_type: string,
        timestamp: string
      }
    }
  | {
      type: "UserJoinedChat",
      data: {
        room_id: string,
        user_info: UserPublicInfo
      }
    }
  | {
      type: "UserLeftChat", 
      data: {
        room_id: string,
        user_id: string
      }
    }
  | {
      type: "NearbyUserDetected",
      data: {
        user_info: UserPublicInfo,
        distance_meters: number
      }
    }
  | {
      type: "NearbyUserLeft",
      data: {
        user_id: string
      }
    }
  | {
      type: "PlaceNotification",
      data: {
        place_id: string,
        notification_type: string,
        content: string,
        metadata?: any
      }
    }
  | {
      type: "MenuUpdate",
      data: {
        company_id: number,
        menu: any,
        timestamp: string
      }
    }
  | {
      type: "OrderStatusUpdate",
      data: {
        session_id: string,
        order_id: string,
        status: string,
        previous_status?: string,
        message?: string,
        updated_at: string,
        order_ref?: string,
        estimated_minutes?: number
      }
    }
  | {
      type: "SubscriptionConfirmed",
      data: {
        subscription_type: string,
        details: string
      }
    }
  | {
      type: "Error",
      data: {
        code: string,
        message: string
      }
    }
  | { type: "Pong" };

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private ws: WebSocket | null = null;
  private messagesSubject = new Subject<ServerMessage>();
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private reconnectTimeoutId: any = null;
  private heartbeatIntervalId: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  
  public messages$ = this.messagesSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  // Observables específicos por tipo de mensaje
  public chatMessages$ = this.messages$.pipe(
    filter(msg => msg.type === 'ChatMessage')
  ) as Observable<Extract<ServerMessage, { type: 'ChatMessage' }>>;

  public proximityNotifications$ = this.messages$.pipe(
    filter(msg => msg.type === 'NearbyUserDetected' || msg.type === 'NearbyUserLeft')
  );

  public placeNotifications$ = this.messages$.pipe(
    filter(msg => msg.type === 'PlaceNotification')
  ) as Observable<Extract<ServerMessage, { type: 'PlaceNotification' }>>;

  public errors$ = this.messages$.pipe(
    filter(msg => msg.type === 'Error')
  ) as Observable<Extract<ServerMessage, { type: 'Error' }>>;

  public menuUpdates$ = this.messages$.pipe(
    filter(msg => msg.type === 'MenuUpdate')
  ) as Observable<Extract<ServerMessage, { type: 'MenuUpdate' }>>;

  public orderStatusUpdates$ = this.messages$.pipe(
    filter(msg => msg.type === 'OrderStatusUpdate')
  ) as Observable<Extract<ServerMessage, { type: 'OrderStatusUpdate' }>>;

  connect(token: string, wsUrl?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // Generar URL automáticamente si no se proporciona
      if (!wsUrl) {
        const baseUrl = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
          ? 'localhost:8080' 
          : window.location.host;
        wsUrl = `${baseUrl}//${host}/api/v1/nats/ws`;
      }
      
      console.log('🔗 Connecting to WebSocket:', wsUrl);
      this.ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
      
      this.ws.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        this.connectionStatusSubject.next(true);
        this.reconnectAttempts = 0;
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: ServerMessage = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', message.type);
          this.messagesSubject.next(message);
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        this.connectionStatusSubject.next(false);
        this.stopHeartbeat();
        
        // Auto-reconnect logic
        if (this.reconnectAttempts < this.maxReconnectAttempts && !event.wasClean) {
          this.scheduleReconnect(token, wsUrl!);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.connectionStatusSubject.next(false);
      };

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
    }
  }

  private scheduleReconnect(token: string, wsUrl: string): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff, max 30s
    console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(token, wsUrl);
    }, delay);
  }

  private startHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
    }

    this.heartbeatIntervalId = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendMessage({ type: 'Ping' });
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  sendMessage(message: ClientMessage): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        console.log('📤 Message sent:', message.type);
        return true;
      } catch (error) {
        console.error('❌ Failed to send message:', error);
        return false;
      }
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message:', message.type);
      return false;
    }
  }

  disconnect(): void {
    console.log('🔌 Manually disconnecting WebSocket');
    
    // Clear reconnection attempts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect

    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  // ==================== MÉTODOS DE CONVENIENCIA ====================

  // Chat methods
  joinChatRoom(roomId: string): boolean {
    return this.sendMessage({ type: 'JoinChatRoom', data: { room_id: roomId } });
  }

  leaveChatRoom(roomId: string): boolean {
    return this.sendMessage({ type: 'LeaveChatRoom', data: { room_id: roomId } });
  }

  // Send chat message
  sendChatMessage(roomId: string, content: string, messageType: string = 'text'): boolean {
    if (!content.trim()) {
      console.warn('Cannot send empty message');
      return false;
    }

    console.log('📤 Sending chat message via WebSocket:', { roomId, content, messageType });
    return this.sendMessage({ 
      type: 'SendChatMessage', 
      data: { 
        room_id: roomId, 
        content: content.trim(), 
        message_type: messageType 
      } 
    });
  }

  // Proximity methods
  enableProximityNotifications(radiusMeters: number = 1000): boolean {
    return this.sendMessage({ 
      type: 'EnableProximityNotifications', 
      data: { radius_meters: radiusMeters } 
    });
  }

  disableProximityNotifications(): boolean {
    return this.sendMessage({ type: 'DisableProximityNotifications' });
  }

  updateLocation(latitude: number, longitude: number): boolean {
    return this.sendMessage({ 
      type: 'UpdateLocation', 
      data: { latitude, longitude } 
    });
  }

  // Place methods
  subscribeToPlace(placeId: string): boolean {
    return this.sendMessage({ type: 'SubscribeToPlace', data: { place_id: placeId } });
  }

  unsubscribeFromPlace(placeId: string): boolean {
    return this.sendMessage({ type: 'UnsubscribeFromPlace', data: { place_id: placeId } });
  }

  // Menu methods
  subscribeToMenu(companyId: number): boolean {
    return this.sendMessage({ type: 'SubscribeToMenu', data: { company_id: companyId } });
  }

  unsubscribeFromMenu(companyId: number): boolean {
    return this.sendMessage({ type: 'UnsubscribeFromMenu', data: { company_id: companyId } });
  }

  // Order status methods
  subscribeToOrderStatus(sessionId: string): boolean {
    return this.sendMessage({ type: 'SubscribeToOrderStatus', data: { session_id: sessionId } });
  }

  unsubscribeFromOrderStatus(sessionId: string): boolean {
    return this.sendMessage({ type: 'UnsubscribeFromOrderStatus', data: { session_id: sessionId } });
  }

  // Status methods
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.ws) return 'CLOSED';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }
}